// Accounts: Foxbeep's company books. Admin only — the menu is shown to Admin only, and every
// table and function behind it refuses anyone else. A light financial workspace inside the
// app's dark navy navigation.
import { useState, useEffect, useCallback, useMemo } from "react";
import { Notice, Button, StatusBadge, EmptyState, SkeletonTiles, SkeletonTable, SkeletonShape } from "./kit/components";
import { T, text } from "./kit/tokens";
import { useMobile } from "../ui/core";
import { loadAccounts, loadWorkspaceExtras, integrityCheck, loadTransaction } from "./api";
import { makeCalendar, fmtDual } from "./bs";
import { SectionCard, errText, readiness, PROVISIONAL_ACTIVATION_NOTE } from "./shared";
import ChartOfAccounts from "./ChartOfAccounts";
import Parties from "./Parties";
import CalendarPeriods from "./CalendarPeriods";
import Taxes from "./Taxes";
import CompanySettings from "./CompanySettings";
import ActivityLog from "./ActivityLog";
import Transactions from "./Transactions";
import Bills from "./Bills";
import Review from "./Review";
import MoneyAccounts from "./MoneyAccounts";
import { Ledger, TrialBalance } from "./Books";
import Journal, { JournalEditor } from "./Journal";
import TxnForm from "./TxnForm";
import StaffCostForm from "./StaffCostForm";
import { isStaffCost, isFreelancer, isOwnerPaid } from "./workflow";
import { FreelancerForm, DirectorPaidForm } from "./PartyMoneyForms";
import EntryDrawer from "./EntryDrawer";
import OpeningBalances from "./OpeningBalances";
import FinancialReports from "./FinancialReports";
import HistoricalBooks from "./HistoricalBooks";

const GROUPS = [
    ["work", "Daily work", [["transactions", "Transactions"], ["bills", "Bills"], ["review", "Review & post"]]],
    ["books", "Books", [["ledger", "Ledger"], ["trial", "Trial balance"], ["reports", "Financial statements"], ["money", "Bank & cash"], ["journal", "Journal"]]],
    ["setup", "Setup", [["overview", "Setup checklist"], ["chart", "Chart of accounts"], ["parties", "People & companies"], ["calendar", "Fiscal years & calendar"],
        ["taxes", "Taxes"], ["settings", "Company & activation"], ["opening", "Opening balances"], ["history", "Historical books"], ["activity", "Activity log"]]],
];
const TAB_KEY = "foxbeep_accounts_tab";
const groupOf = (tab) => GROUPS.find(([, , items]) => items.some(([k]) => k === tab))?.[0];

function Nav({ tab, go, has2B, has2C, has2F, mobile }) {
    const group = groupOf(tab) || "setup";
    const groups = (has2B ? GROUPS : GROUPS.filter(([g]) => g === "setup"))
        .map(([g, label, its]) => [g, label, its.filter(([k]) => (has2C || k !== "opening") && (has2F || (k !== "reports" && k !== "history")))]);
    const items = groups.find(([g]) => g === group)?.[2] || [];
    const pill = (active) => ({
        border: "none", cursor: "pointer", fontFamily: T.font, whiteSpace: "nowrap", borderRadius: 999, padding: mobile ? "7px 12px" : "7px 14px",
        fontSize: 13, fontWeight: 600, background: active ? T.primary : "transparent", color: active ? "#fff" : T.textMuted,
    });
    return (
        <nav aria-label="Accounts sections" style={{ display: "grid", gap: 10, marginBottom: 22 }}>
            {groups.length > 1 && (
                <div role="tablist" style={{ display: "flex", gap: 2, borderBottom: `1px solid ${T.border}`, overflowX: "auto" }}>
                    {groups.map(([g, label, its]) => {
                        const active = g === group;
                        return <button key={g} role="tab" aria-selected={active} onClick={() => go(its[0][0])}
                            style={{ border: "none", background: "transparent", cursor: "pointer", fontFamily: T.font, padding: "10px 14px", fontSize: 14, fontWeight: 650,
                                color: active ? T.textStrong : T.textMuted, borderBottom: `2px solid ${active ? T.primary : "transparent"}`, marginBottom: -1, whiteSpace: "nowrap" }}>{label}</button>;
                    })}
                </div>
            )}
            <div role="tablist" style={{ display: "flex", gap: 4, overflowX: "auto", paddingBottom: 2 }}>
                {items.map(([k, label]) => <button key={k} role="tab" aria-selected={tab === k} onClick={() => go(k)} style={pill(tab === k)}>{label}</button>)}
            </div>
        </nav>
    );
}

export default function AccountsPage() {
    const mobile = useMobile();
    const [tab, setTab] = useState(() => { try { return sessionStorage.getItem(TAB_KEY) || ""; } catch { return ""; } });
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [version, setVersion] = useState(0);
    const [form, setForm] = useState(null);          // initial values for TxnForm, or null
    const [entryId, setEntryId] = useState(null);
    const [journalOpen, setJournalOpen] = useState(false);
    const reload = useCallback(async () => {
        try {
            setError("");
            const base = await loadAccounts();
            setData(base.installed ? { ...base, ...(await loadWorkspaceExtras()) } : base);
            setVersion(v => v + 1);
        } catch (e) { setError(errText(e)); }
    }, []);
    useEffect(() => { reload(); }, [reload]);
    const go = (t) => { setTab(t); try { sessionStorage.setItem(TAB_KEY, t); } catch { /* private mode */ } };
    const cal = useMemo(() => makeCalendar(data?.calendar || []), [data]);
    const activeTab = tab || (data?.has2B && data?.settings?.enabled ? "transactions" : "overview");

    const shell = (children) => (
        <div style={{ margin: mobile ? "-14px -14px 0" : "-32px -32px 0", padding: mobile ? "18px 14px 28px" : "30px 32px 40px", background: T.bg, color: T.text,
            minHeight: mobile ? "calc(100vh - 60px)" : "100vh", fontFamily: T.font }}>
            <div style={{ maxWidth: 1240, margin: "0 auto" }}>{children}</div>
        </div>
    );
    const header = (
        <div style={{ display: "flex", gap: 12, alignItems: mobile ? "flex-start" : "center", justifyContent: "space-between", flexDirection: mobile ? "column" : "row", marginBottom: 18 }}>
            <div>
                <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 1.2, color: T.accent, textTransform: "uppercase" }}>Foxbeep Technology</div>
                <h2 style={{ ...text.pageTitle, fontSize: mobile ? 22 : 26 }}>Accounts</h2>
            </div>
            {!data && <div style={{ display: "flex", gap: 8, alignItems: "center", minHeight: 40 }}><SkeletonShape radius={999}><StatusBadge status="on" label="Books are open" /></SkeletonShape></div>}
            {data?.installed && (
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", minHeight: 40 }}>
                    <StatusBadge status={data.settings?.enabled ? "on" : "off"} label={data.settings?.enabled ? "Books are open" : "Accounts is off"} />
                    {data.settings?.enabled && readiness(data, cal).provisionalActivation && <StatusBadge status="unverified" label="Accounts ON — PROVISIONAL (auditor chart review pending)" />}
                    {data.has2B && data.settings?.enabled && <Button onClick={() => setForm({})}>+ New</Button>}
                </div>
            )}
        </div>
    );
    if (error) return shell(<>{header}<Notice tone="danger" action={<Button size="sm" variant="secondary" onClick={reload}>Try again</Button>}>Accounts could not be loaded: {error}</Notice></>);
    if (!data) return shell(<>{header}
        <div aria-hidden="true" style={{ display: "grid", gap: 10, marginBottom: 22 }}>
            <div style={{ display: "flex", gap: 2, borderBottom: `1px solid ${T.border}`, overflow: "hidden" }}>
                {["Daily work", "Books", "Setup"].map(l => <div key={l} style={{ padding: "10px 14px", fontSize: 14, fontWeight: 650, borderBottom: "2px solid transparent", marginBottom: -1 }}>
                    <SkeletonShape radius={6}><span>{l}</span></SkeletonShape></div>)}
            </div>
            <div style={{ display: "flex", gap: 4, overflow: "hidden", paddingBottom: 2 }}>
                {["Transactions", "Bills", "Review & post"].map(l => <SkeletonShape key={l} radius={999}><span style={{ display: "inline-block", padding: mobile ? "7px 12px" : "7px 14px", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>{l}</span></SkeletonShape>)}
            </div>
        </div>
        <div style={{ display: "grid", gap: 18 }}>
            <SkeletonTiles count={4} />
            <SkeletonTable rows={6} cols={5} />
        </div></>);
    if (!data.installed) return shell(<>{header}
        <SectionCard title="Accounts isn't set up on this server yet">
            <EmptyState title="The accounting database has not been installed here." hint="Nothing to do on this page until the Accounts update is approved and applied." />
        </SectionCard></>);

    const openEdit = async (t) => {
        try { setEntryId(null); setForm(t.__reloadId ? await loadTransaction(t.__reloadId) : t); } catch (e) { setError(errText(e)); }
    };
    // Opening balances pending (011, derived from the ledger): books are provisional until the opening package is posted.
    const provisional = !!(data.has2C && data.opening?.pending && data.settings?.enabled);
    // Provisional activation (021): on without the auditor's chart review; every export says so.
    const provisionalActivation = readiness(data, cal).provisionalActivation;
    const exportNote = [provisional && "PROVISIONAL – opening balances not posted", provisionalActivation && PROVISIONAL_ACTIVATION_NOTE].filter(Boolean).join("; ") || undefined;
    const ctx = {
        data, cal, reload, go, version, mobile, provisional, provisionalActivation, exportProvisional: provisional || provisionalActivation, exportNote,
        display: data.settings?.date_display || "BS",
        openNew: (initial) => setForm(initial || {}), openEntry: setEntryId, openJournal: () => setJournalOpen(true),
    };
    return shell(<>
        {header}
        <Nav tab={activeTab} go={go} has2B={data.has2B} has2C={data.has2C} has2F={data.has2F} mobile={mobile} />
        {provisionalActivation && (
            <div role="status" style={{ marginBottom: 18 }}>
                <Notice tone="warning" action={<Button size="sm" variant="secondary" onClick={() => go("settings")}>Company & activation</Button>}>
                    <b>Accounts is on provisionally.</b> Switched on by a Foxbeep management approval; the auditor's chart-of-accounts review is still <b>pending</b> (not an audit, not an auditor's approval). The year can't be closed until the review is recorded.
                </Notice>
            </div>
        )}
        {provisional && (
            <div role="status" style={{ marginBottom: 18 }}>
                <Notice tone="warning" action={<Button size="sm" variant="secondary" onClick={() => go("opening")}>Opening balances</Button>}>
                    <b>Opening balances pending auditor approval.</b> Balances and reports are incomplete and provisional; bank and cash show movements since the books start date only. Months can't be locked until the opening package is posted.
                </Notice>
            </div>
        )}
        {activeTab === "transactions" && <Transactions ctx={ctx} />}
        {activeTab === "bills" && <Bills ctx={ctx} />}
        {activeTab === "review" && <Review ctx={ctx} />}
        {activeTab === "ledger" && <Ledger ctx={ctx} />}
        {activeTab === "trial" && <TrialBalance ctx={ctx} />}
        {activeTab === "money" && <MoneyAccounts ctx={ctx} />}
        {activeTab === "journal" && <Journal ctx={ctx} />}
        {activeTab === "overview" && <Overview ctx={ctx} />}
        {activeTab === "chart" && <ChartOfAccounts ctx={ctx} />}
        {activeTab === "parties" && <Parties ctx={ctx} />}
        {activeTab === "calendar" && <CalendarPeriods ctx={ctx} />}
        {activeTab === "taxes" && <Taxes ctx={ctx} />}
        {activeTab === "settings" && <CompanySettings ctx={ctx} />}
        {activeTab === "activity" && <ActivityLog ctx={ctx} />}
        {activeTab === "opening" && data.has2C && <OpeningBalances ctx={ctx} />}
        {activeTab === "reports" && data.has2F && <FinancialReports ctx={ctx} />}
        {activeTab === "history" && data.has2F && <HistoricalBooks ctx={ctx} />}
        {data.has2B && <>
            <TxnForm open={!!form && !form.__staff && !isStaffCost(form) && !form.__freelancer && !isFreelancer(form) && !form.__ownerpaid && !isOwnerPaid(form)} ctx={ctx} initial={form && Object.keys(form).length ? form : null} onClose={() => setForm(null)}
                onAdvanced={() => { setForm(null); setJournalOpen(true); }} onStaffCost={() => setForm({ __staff: true })}
                onFreelancer={() => setForm({ __freelancer: true })} onDirectorPaid={() => setForm({ __ownerpaid: true })}
                onSaved={async (res) => { setForm(null); await reload(); setEntryId(res.entry_id); }} />
            <StaffCostForm open={!!form && (!!form.__staff || isStaffCost(form))} ctx={ctx} initial={form && isStaffCost(form) ? form : null} onClose={() => setForm(null)}
                onSaved={async (res) => { setForm(null); await reload(); setEntryId(res.entry_id); }} />
            <FreelancerForm open={!!form && (!!form.__freelancer || isFreelancer(form))} ctx={ctx} initial={form && isFreelancer(form) ? form : null} onClose={() => setForm(null)}
                onSaved={async (res) => { setForm(null); await reload(); setEntryId(res.entry_id); }} />
            <DirectorPaidForm open={!!form && (!!form.__ownerpaid || isOwnerPaid(form))} ctx={ctx} initial={form && isOwnerPaid(form) ? form : null} onClose={() => setForm(null)}
                onSaved={async (res) => { setForm(null); await reload(); setEntryId(res.entry_id); }} />
            {entryId && <EntryDrawer entryId={entryId} ctx={ctx} onClose={() => setEntryId(null)} onChanged={reload}
                onEdit={openEdit} onPayBill={(b) => { setEntryId(null); setForm({ kind: b.kind === "freelancer_bill" ? "freelancer_bill_payment" : "bill_payment", bill_id: b.id }); }} />}
            <JournalEditor open={journalOpen} ctx={ctx} onClose={() => setJournalOpen(false)}
                onSaved={async (id) => { setJournalOpen(false); await reload(); setEntryId(id); }} />
        </>}
    </>);
}

// ─── Setup overview ─────────────────────────────────────────────────────────
function Step({ n, title, detail, state, action }) {
    const tone = state === "done" ? T.success : state === "wait" ? T.warning : state === "info" ? T.primary : T.textFaint;
    const label = state === "done" ? "Done" : state === "wait" ? "Waiting" : state === "info" ? "Note" : "To do";
    return (
        <li style={{ display: "flex", gap: 12, padding: "14px 0", borderBottom: `1px solid ${T.borderSoft}`, alignItems: "flex-start", flexWrap: "wrap" }}>
            <span style={{ width: 26, height: 26, borderRadius: 13, flexShrink: 0, display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700,
                background: tone + "22", color: tone, border: `1px solid ${tone}55` }}>{state === "done" ? "✓" : n}</span>
            <div style={{ flex: "1 1 260px", minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: T.textStrong, fontSize: 14 }}>{title} <span style={{ fontSize: 12, color: tone, fontWeight: 600, marginLeft: 6 }}>{label}</span></div>
                <div style={{ ...text.caption, marginTop: 4, lineHeight: 1.5 }}>{detail}</div>
            </div>
            {action}
        </li>
    );
}

const CHECK_LABELS = {
    "posted entries balance": "Every posted entry balances (debits = credits)",
    "posted numbers are contiguous": "Entry numbers run without gaps",
    "no number beyond its counter": "No entry number was issued twice",
    "posted entries unchanged since posting (hash chain)": "No posted entry has been altered",
    "audit log hash chain intact": "The activity log has not been altered",
    "reversals mirror their originals": "Every correction exactly reverses its original",
    "trial balance (Σ debit − Σ credit = 0)": "The books balance overall",
};

function Overview({ ctx }) {
    const { data, cal, go, display } = ctx;
    const s = data.settings;
    const r = readiness(data, cal);
    const [checks, setChecks] = useState(null);
    const [checking, setChecking] = useState(false);
    const [checkErr, setCheckErr] = useState("");
    const runCheck = async () => {
        setChecking(true); setCheckErr("");
        try { setChecks(await integrityCheck()); } catch (e) { setCheckErr(errText(e)); } finally { setChecking(false); }
    };
    const open = (t, label = "Open") => <Button size="sm" variant="secondary" onClick={() => go(t)}>{label}</Button>;
    const vatCodes = data.taxCodes.filter(c => c.kind === "vat");
    const activeTax = data.taxCodes.filter(c => c.active).length;

    return (
        <div>
            <div style={{ marginBottom: 20 }}>
                {s.enabled
                    ? <Notice tone={r.provisionalActivation ? "warning" : "success"}>Accounts is <b>on{r.provisionalActivation ? " provisionally" : ""}</b>. Entries can be posted in open periods from {fmtDual(s.books_start_date, cal, display)}.{r.provisionalActivation ? " The auditor's chart-of-accounts review is still pending." : ""}</Notice>
                    : <Notice tone="info">Accounts is <b>off</b>. Nothing can be posted until the steps below are complete and Accounts is deliberately switched on. Setting things up here doesn't create any accounting entries.</Notice>}
            </div>

            <SectionCard title="Getting the books ready" subtitle="Work through these in order. Steps marked Waiting depend on your auditor or on official sources.">
                <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    <Step n={1} title="Company details" state={r.pan ? "done" : "todo"}
                        detail={r.pan ? `${s.company_name} · PAN on file` : `${s.company_name} · PAN not entered yet. It is entered in the app, never written into code or documents.`}
                        action={open("settings")} />
                    <Step n={2} title="Check the Nepali calendar against the official calendar" state={r.fyVerified ? "done" : "wait"}
                        detail={`${r.verifiedMonths} of ${r.totalMonths} loaded months verified. Months come from two open-source calendars that agree; each still needs checking against the official calendar. Jestha and Ashadh 2084 are not loaded because the sources disagree, so FY 2083/84 can't be created yet.`}
                        action={open("calendar")} />
                    <Step n={3} title="Choose the books start date" state={r.start ? "done" : "wait"}
                        detail={r.start ? `Books start ${fmtDual(r.start, cal, display)}.` : "Set from the auditor-approved closing figures. Until then no opening balances are entered and nothing can be posted."}
                        action={open("settings")} />
                    <Step n={4} title="Create the fiscal year that contains the start date" state={r.fy ? "done" : "todo"}
                        detail={r.fy ? `FY ${r.fy.label} exists${r.fyVerified ? " and all 12 months are verified" : " — some of its months are not verified yet"}.`
                            : data.years.length ? `Fiscal years created: ${data.years.map(y => y.label).join(", ")}. None contains the books start date yet.` : "No fiscal year created yet."}
                        action={open("calendar")} />
                    <Step n={5} title="Chart of accounts reviewed by the auditor" state={r.chartReview ? "done" : "wait"}
                        detail={r.chartReview ? `Review recorded: ${r.chartReview.auditor_name}, ${r.chartReview.firm} (ref ${r.chartReview.reference}).`
                            : `${r.provisionalApproval ? `Pending — Accounts may be switched on provisionally (management approval ref ${r.provisionalApproval.reference}); this is not the auditor's review. ` : ""}${data.accounts.length} accounts loaded (draft for review). Rent accounts stay inactive until the rent decision is made. Record the auditor's review under Company & activation.`}
                        action={open(data.has2C ? "settings" : "chart")} />
                    <Step n={6} title="Taxes" state="info"
                        detail={`${s.vat_registered ? "VAT registered" : "Not VAT-registered (PAN only)"} · ${vatCodes.length} VAT codes kept switched off · ${activeTax} tax codes active. Withholding (TDS) rates are added only after checking current IRD rules.`}
                        action={open("taxes")} />
                    <Step n={7} title="Switch Accounts on" state={s.enabled ? "done" : r.canEnable ? "todo" : "wait"}
                        detail={s.enabled ? (r.provisionalActivation ? "Accounts is on provisionally (auditor's chart review pending)." : "Accounts is on.") : r.canEnable ? "Everything required is in place. Switching on needs a written reason." : r.activation ? `${r.activation.filter(x => !x.ok).length} of ${r.activation.length} requirements still open (checked by the database). See Company & activation.` : "Available once the PAN, books start date and a fully verified fiscal year are in place."}
                        action={open("settings")} />
                    {data.has2C && <Step n={8} title="Opening balances (auditor-approved)" state={s.enabled && !data.opening?.pending ? "done" : s.enabled ? "todo" : "wait"}
                        detail={!s.enabled ? "Prepared after switching on: import the auditor's closing figures, tie out, record the auditor's sign-off, then post the package."
                            : data.opening?.pending ? "Pending: the books are provisional until the auditor-approved opening package is posted." : "Posted."}
                        action={s.enabled ? open("opening") : null} />}
                </ol>
            </SectionCard>

            <SectionCard title="Check the books" subtitle="Recalculates every safeguard from the stored data: balancing, numbering, and that nothing posted or logged has been altered."
                actions={<Button size="sm" onClick={runCheck} disabled={checking}>{checking ? "Checking…" : "Run check"}</Button>}>
                {checkErr && <Notice tone="danger">{checkErr}</Notice>}
                {!checks && !checkErr && <div style={text.caption}>{data.postedCount ? `${data.postedCount} posted entries.` : "No entries have been posted yet."} Run the check at any time.</div>}
                {checks && (
                    <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
                        {checks.map(c => (
                            <li key={c.check_name} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 14, flexWrap: "wrap" }}>
                                <StatusBadge status={c.status === "OK" ? "confirmed" : c.status === "WARN" ? "unverified" : "review"} label={c.status === "OK" ? "OK" : c.status === "WARN" ? "Check" : "Problem"} />
                                <span style={{ color: T.text }}>{CHECK_LABELS[c.check_name] || c.check_name}</span>
                                {c.status !== "OK" && c.detail && <span style={text.caption}>{c.detail}</span>}
                            </li>
                        ))}
                    </ul>
                )}
            </SectionCard>

            <SectionCard title="At a glance">
                <div style={{ display: "grid", gridTemplateColumns: ctx.mobile ? "1fr 1fr" : "repeat(4, minmax(0,1fr))", gap: 12 }}>
                    {[["Accounts", data.accounts.filter(a => a.is_postable && a.active).length, "active posting accounts"],
                      ["People & companies", data.parties.filter(p => p.active).length, "active"],
                      ["Fiscal years", data.years.length, data.years.length ? data.years.map(y => y.label).join(", ") : "none yet"],
                      ["Posted entries", data.postedCount, "journal entries arrive in the next stage"]].map(([k, v, sub]) => (
                        <div key={k} style={{ background: T.surface2, border: `1px solid ${T.borderSoft}`, borderRadius: T.radiusSm, padding: 12, minWidth: 0 }}>
                            <div style={text.caption}>{k}</div>
                            <div style={{ fontSize: 22, fontWeight: 700, color: T.textStrong, fontVariantNumeric: "tabular-nums" }}>{v}</div>
                            <div style={{ ...text.caption, overflowWrap: "anywhere" }}>{sub}</div>
                        </div>
                    ))}
                </div>
            </SectionCard>
        </div>
    );
}
